import { NavItem } from 'src/app/shared/api/types/interfaces';
import { EnvironmentEnum, NavSectionEnum, NavValueEnum, RoleNames, RoutePaths } from 'src/app/shared/api/types/enums';

export type NavSection = {
  [key in NavSectionEnum]: NavItem[];
};

/**
 * EXCLUSIVE_ACCORDION — opening a rail section closes the open one.
 * Flipping to multi-open later is this one line (mirrors the same constant in
 * the canonical /mockups/shell/ia implementation).
 */
export const EXCLUSIVE_ACCORDION = true;

export const NAV_DATA: NavSection = {
  [NavSectionEnum.Home]: [{ value: NavValueEnum.Home, path: RoutePaths.Home }],
  [NavSectionEnum.Sales]: [
    { value: NavValueEnum.AiActionFeed, path: RoutePaths.AiActionFeed },
    { value: NavValueEnum.Companies, path: RoutePaths.Companies, role: RoleNames.user },
    { value: NavValueEnum.Requests, path: RoutePaths.RequestsSp, role: RoleNames.order },
    { value: NavValueEnum.Quotes, path: RoutePaths.QuotesSp, role: RoleNames.order },
    { value: NavValueEnum.Orders, path: RoutePaths.OrdersSp, role: RoleNames.order },
    { value: NavValueEnum.ProductCatalogue, path: RoutePaths.ProductCatalogue, role: RoleNames.product }
  ],
  [NavSectionEnum.Orders]: [{ value: NavValueEnum.Orders, path: RoutePaths.OrdersBo, role: RoleNames.order }],
  [NavSectionEnum.PIM]: [
    { value: NavValueEnum.ProductsAndClusters, path: RoutePaths.PimProductsAndClusters, role: RoleNames.product },
    { value: NavValueEnum.Categories, path: RoutePaths.PimCategories, role: RoleNames.product },
    { value: NavValueEnum.ClusterDefinitions, path: RoutePaths.PimClusterDefinition, role: RoleNames.product }
  ],
  [NavSectionEnum.Users]: [
    { value: NavValueEnum.Users, path: RoutePaths.UserManagementUsers, role: RoleNames.user },
    {
      value: NavValueEnum.Favorites,
      path: RoutePaths.UserManagementFavorites,
      role: [RoleNames.shop, RoleNames.product]
    },
    {
      value: NavValueEnum.OrderLists,
      path: RoutePaths.UserManagementOrderlists,
      role: [RoleNames.shop, RoleNames.product, RoleNames.user]
    },
    {
      value: NavValueEnum.LoginTokens,
      path: RoutePaths.UserManagementLoginTokens,
      role: RoleNames.configuration,
      env: [EnvironmentEnum.Development, EnvironmentEnum.Staging, EnvironmentEnum.Production]
    }
  ],
  [NavSectionEnum.Pricing]: [
    { value: NavValueEnum.Pricesheets, path: RoutePaths.PricingPricesheets, role: RoleNames.pricing },
    { value: NavValueEnum.Taxes, path: RoutePaths.PricingTaxes, role: RoleNames.pricing },
    { value: NavValueEnum.Surcharges, path: RoutePaths.PricingSurcharges, role: RoleNames.pricing }
  ],
  [NavSectionEnum.ShopsAndChannels]: [
    { value: NavValueEnum.Channels, path: RoutePaths.SacChannels, role: RoleNames.configuration },
    { value: NavValueEnum.Carriers, path: RoutePaths.SacCarriers, role: RoleNames.logistics },
    { value: NavValueEnum.Warehouses, path: RoutePaths.SacWarehouses, role: RoleNames.logistics },
    { value: NavValueEnum.OrderStatuses, path: RoutePaths.SacOrderStatuses, role: RoleNames.order },
    { value: NavValueEnum.PaymentMethods, path: RoutePaths.SacPaymentMethods, role: RoleNames.shop },
    {
      value: NavValueEnum.Templates,
      path: RoutePaths.SacTemplates,
      role: RoleNames.configuration
    },
    {
      value: NavValueEnum.EventActions,
      path: RoutePaths.SacEventActions,
      role: RoleNames.configuration,
      env: [EnvironmentEnum.Development, EnvironmentEnum.Staging, EnvironmentEnum.Production]
    },
    // One screen for all five rule types (NBO-2446). Gated on configuration like the backend's rule
    // operations, whatever the type — carrier and payment-method rules no longer inherit the
    // logistics / shop roles of the screens whose tabs they used to sit in.
    { value: NavValueEnum.BusinessRules, path: RoutePaths.SacBusinessRules, role: RoleNames.configuration }
  ],
  [NavSectionEnum.Admin]: [
    { value: NavValueEnum.BackofficeUsers, path: RoutePaths.AdminBackofficeUsers, role: RoleNames.role },
    { value: NavValueEnum.Agents, path: RoutePaths.AdminAgents, role: RoleNames.configuration },
    { value: NavValueEnum.Attributes, path: RoutePaths.AdminAttributes, role: RoleNames.configuration },
    { value: NavValueEnum.Valuesets, path: RoutePaths.AdminValueset, role: RoleNames.configuration },
    { value: NavValueEnum.DataImport, path: RoutePaths.AdminDataImport, role: RoleNames.configuration },
    { value: NavValueEnum.ApiKeys, path: RoutePaths.AdminApiKeys, role: RoleNames.apikey },
    // Support tooling (NBO-2465): the backend lets any product owner call these operations, so this
    // flag and the route's guard are what keep the screen to platform staff.
    { value: NavValueEnum.SearchIndex, path: RoutePaths.AdminSearchIndex, staffOnly: true }
  ],
  [NavSectionEnum.Playground]: [
    { value: NavValueEnum.Playground, path: RoutePaths.Playground, env: [EnvironmentEnum.Development] },
    { value: NavValueEnum.UnitTest, path: RoutePaths.TestResult, env: [EnvironmentEnum.Development] }
  ]
};

/**
 * The accordion rail's world → section tree (NBO-2331, /mockups/shell/ia
 * Direction C). Items come straight from NAV_DATA so there is exactly one
 * source of truth for what is navigable (the roles/permissions preview screen
 * reads NAV_DATA too); the worlds add the icon-rail glyphs and the
 * section-level role gates that used to live on globals.spNavItems /
 * boNavItems. The Back Office world carries the RoleNames.backoffice gate the
 * old icon rail applied to every BO entry.
 */
export interface RailSection {
  key: NavSectionEnum;
  /** Material Symbols glyph shown in the section header + collapsed rail. */
  icon: string;
  items: NavItem[];
  /** Section-level role gate (any-of). Items keep their own gates. */
  role?: RoleNames[];
  env?: EnvironmentEnum[];
}

export interface RailWorld {
  key: 'sales' | 'back';
  labelKey: string;
  role?: RoleNames[];
  sections: RailSection[];
}

export const RAIL_WORLDS: RailWorld[] = [
  {
    key: 'sales',
    labelKey: 'nav.sections.sales_hub',
    sections: [
      { key: NavSectionEnum.Home, icon: 'home', items: NAV_DATA[NavSectionEnum.Home] },
      { key: NavSectionEnum.Sales, icon: 'shopping_cart', role: [RoleNames.sales], items: NAV_DATA[NavSectionEnum.Sales] }
    ]
  },
  {
    key: 'back',
    labelKey: 'nav.sections.back_office',
    role: [RoleNames.backoffice],
    sections: [
      { key: NavSectionEnum.Orders, icon: 'list_alt', role: [RoleNames.order], items: NAV_DATA[NavSectionEnum.Orders] },
      { key: NavSectionEnum.PIM, icon: 'grid_view', role: [RoleNames.product], items: NAV_DATA[NavSectionEnum.PIM] },
      {
        key: NavSectionEnum.Users,
        icon: 'group',
        role: [RoleNames.user, RoleNames.configuration, RoleNames.shop],
        items: NAV_DATA[NavSectionEnum.Users]
      },
      { key: NavSectionEnum.Pricing, icon: 'local_offer', role: [RoleNames.pricing], items: NAV_DATA[NavSectionEnum.Pricing] },
      {
        key: NavSectionEnum.ShopsAndChannels,
        icon: 'storefront',
        role: [RoleNames.pricing, RoleNames.shop, RoleNames.configuration, RoleNames.logistics],
        items: NAV_DATA[NavSectionEnum.ShopsAndChannels]
      },
      {
        key: NavSectionEnum.Admin,
        icon: 'settings',
        role: [RoleNames.configuration, RoleNames.role],
        items: NAV_DATA[NavSectionEnum.Admin]
      },
      {
        key: NavSectionEnum.Playground,
        icon: 'tune',
        env: [EnvironmentEnum.Development],
        items: NAV_DATA[NavSectionEnum.Playground]
      }
    ]
  }
];

/**
 * Every nav leaf, flattened — the rail resolves the active route by matching
 * the URL against these paths (longest prefix wins), so a detail page keeps its
 * parent item lit. Derived from RAIL_WORLDS so it cannot drift.
 */
export const ALL_NAV_ITEMS: NavItem[] = RAIL_WORLDS.flatMap((world) =>
  world.sections.flatMap((section) => section.items)
);
